import { useCallback, useEffect, useState } from 'react';
import { Loader2, Megaphone, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_ANNOUNCEMENTS,
  getAnnouncements,
  saveAnnouncements,
} from '../../database/serverAnnouncements';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import PageHeader from './shell/PageHeader';

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const MAX_MESSAGES = 20;

const ServerAnnouncementsManagement = () => {
  const [form, setForm] = useState(DEFAULT_ANNOUNCEMENTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setForm(await getAnnouncements());
    } catch (err) {
      toast.error('Could not load announcements.', { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateMessage = (index, value) =>
    setForm((prev) => ({
      ...prev,
      messages: prev.messages.map((m, i) => (i === index ? value : m)),
    }));

  const removeMessage = (index) =>
    setForm((prev) => ({ ...prev, messages: prev.messages.filter((_, i) => i !== index) }));

  const addMessage = () => setForm((prev) => ({ ...prev, messages: [...prev.messages, ''] }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      setForm(await saveAnnouncements(form));
      toast.success('Announcements saved.');
    } catch (err) {
      toast.error('Could not save announcements.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader eyebrow="Server announcements" title="Server announcements" />
        <div className="h-40 animate-pulse rounded-md border bg-muted/40" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Server"
        title="Server announcements"
        description="Rotating messages the server posts to in-game chat on a fixed interval."
        breadcrumbs={[
          { label: 'Admin', to: '/admin/dashboard' },
          { label: 'Server announcements' },
        ]}
      />

      <div className="space-y-6 pb-16">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Megaphone className="h-4 w-4 text-primary" />
              Announcements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="flex items-center justify-between rounded-md border px-4 py-3">
                <div>
                  <div className="text-sm font-medium">Announcements enabled</div>
                  <div className="text-xs text-muted-foreground">
                    Off stops the server from posting any messages.
                  </div>
                </div>
                <Switch
                  checked={Boolean(form.enabled)}
                  onCheckedChange={(enabled) => setForm((p) => ({ ...p, enabled }))}
                />
              </div>

              <Field
                label="Interval (minutes)"
                hint="Minutes between messages. One message per interval, rotating."
              >
                <Input
                  type="number"
                  min="1"
                  max="1440"
                  value={form.intervalMinutes}
                  onChange={(e) => setForm((p) => ({ ...p, intervalMinutes: e.target.value }))}
                  className="max-w-[10rem]"
                />
              </Field>

              <div className="space-y-3">
                {form.messages.map((message, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={message}
                      maxLength={200}
                      onChange={(e) => updateMessage(i, e.target.value)}
                      placeholder="Message text"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeMessage(i)}
                      aria-label="Remove message"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addMessage}
                disabled={form.messages.length >= MAX_MESSAGES}
              >
                <Plus className="h-4 w-4" />
                Add message
              </Button>

              <div>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ServerAnnouncementsManagement;
